import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { UploadSimple, X } from '@phosphor-icons/react';
import { collectDroppedFiles } from '../../lib/spine-detector';
import SmartAssetImporter from '../sidebar/SmartAssetImporter';

interface DragDropImportOverlayProps {
  onImportComplete?: () => void;
}

/**
 * Page-level drag-and-drop overlay for importing Spine assets.
 * Listens to window drag events; only activates when OS files are dragged
 * (not internal app drags). Shows a full-screen drop prompt, then opens the
 * SmartAssetImporter dialog with the dropped files pre-loaded.
 */
export default function DragDropImportOverlay({ onImportComplete }: DragDropImportOverlayProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const counter = useRef(0);

  useEffect(() => {
    function isFileDrag(e: DragEvent): boolean {
      return Array.from(e.dataTransfer?.types ?? []).includes('Files');
    }

    function onDragEnter(e: DragEvent) {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counter.current++;
      if (counter.current === 1) setIsDragging(true);
    }

    function onDragLeave() {
      counter.current = Math.max(0, counter.current - 1);
      if (counter.current === 0) setIsDragging(false);
    }

    function onDragOver(e: DragEvent) {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }

    async function onDrop(e: DragEvent) {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counter.current = 0;
      setIsDragging(false);
      if (!e.dataTransfer) return;
      const files = await collectDroppedFiles(e.dataTransfer);
      if (files.length > 0) {
        setPendingFiles(files);
        setDialogOpen(true);
      }
    }

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  function closeDialog() {
    setDialogOpen(false);
    setPendingFiles([]);
  }

  return (
    <>
      {/* Full-screen drop overlay */}
      {isDragging && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-none">
          <div className="absolute inset-3 rounded-2xl border-2 border-dashed border-accent bg-bg-deepest/85 backdrop-blur-sm" />
          <div className="relative flex flex-col items-center gap-3 text-center">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center">
              <UploadSimple size={40} weight="thin" className="text-accent" />
            </div>
            <p className="font-sans text-xl font-semibold text-text-primary">Drop to Import</p>
            <p className="font-sans text-sm text-text-secondary">
              Spine exports (.json + .atlas + .png) detected automatically
            </p>
          </div>
        </div>
      )}

      {/* Import dialog */}
      <Dialog.Root open={dialogOpen} onOpenChange={o => { if (!o) closeDialog(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/75 z-[210]" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[220] w-[min(480px,calc(100vw-32px))] bg-bg-elevated border border-border rounded-xl shadow-2xl focus:outline-none flex flex-col max-h-[80vh]"
            onPointerDownOutside={e => e.preventDefault()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-subtle shrink-0">
              <div>
                <Dialog.Title className="font-sans font-semibold text-sm text-text-primary">
                  Import Assets
                </Dialog.Title>
                <Dialog.Description className="font-sans text-xs text-text-tertiary mt-0.5">
                  Detected from dropped files
                </Dialog.Description>
              </div>
              <Dialog.Close className="w-7 h-7 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors">
                <X size={14} />
              </Dialog.Close>
            </div>
            <div className="overflow-y-auto flex-1">
              {dialogOpen && (
                <SmartAssetImporter
                  initialFiles={pendingFiles}
                  onImportComplete={() => {
                    closeDialog();
                    onImportComplete?.();
                  }}
                />
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
